import React, { useRef, useState, useEffect } from "react";
import * as Yup from "yup";
import FormBuilder from "../../../../shared/components/form-builder/form-builder";
import { Box, Typography } from "@mui/material";
import { StyledButton } from "../../../../shared/components/buttons/custom-button";
import {
  buyerSellerDetailsService,
  mlbuyerSellerDetailsService,
} from "../../../transaction-certificates/tc-service/buyer-seller-details.service";
import { useApi } from "../../../../shared/hooks/useApi";
import { TCAppConfig } from "../../../../shared/app-configs/tc-app-configs";
import { isAdmin, isViewOnlyAdminMode } from "../../../../shared/utils/admin-utils";

import { LoaderComponent } from "../../../../shared/components/loaders/page-loader";
import CustomTable from "../../../../shared/components/table/custom-table";
import { Pagination } from "../../../../shared/hooks/useRecordStore";
import { MRT_ColumnDef } from "material-react-table";
import ActionsModalPopup, {
  ActionButtonConfig,
} from "../../../../shared/components/modals/action-popup";
import CustomStepper from "../../../../shared/components/stepper/steppers";
import useNotification from "../../../../shared/hooks/useNotification";
import { InventoryFIlesView } from "../../../transaction-certificates/tc-dashboard/cbLandingPageTable.config";
import { ActionsMenu } from "../../../../shared/components/table/custom-actions";
import { mapApiResponseToRawMaterial, RawMaterialRecords } from "./models/raw-material-summary-model";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { dtrackItService } from "../../../transaction-certificates/tc-service/dtrackit.service";
import { useTransactionCertificateStore } from "../../../../store/transaction-certificates.store";
import { PageWrapper } from "../material-ledger-summary";
import { extractTEIdFromEmail } from "../../../../shared/utils/user-utils";
import { useRecentTEIds } from "../../../../shared/hooks/useRecentTEIds";

const RawMaterialSummarry = () => {
  const formRef = useRef<any>(null);
  const { notify } = useNotification();
  const { executeGet, loading } = useApi();
  const [isResetEnabled, setResetEnable] = useState(false);

  const [data, setData] = useState<RawMaterialRecords>([]);
  const [pagination, setPagination] = useState<Pagination>({
    pageIndex: 0,
    pageSize: 10,
  });
  const navigate = useNavigate();
  const [rowCount, setRowCount] = useState<number>(0);
  const [showTable, setShowTable] = useState(false);
  const [visible, setVisible] = useState({
    coName: true,
    coTeId: true,
    coNameLabel: true,
    coTeIdLabel: true,
  });

  const [disable, setDisable] = useState({
    coName: false,
    coTeId: false,
    rmCode: true,
  });

  const [rawMaterials, setRawMaterials] = useState<any[]>([]);
  const [certifiedRawMaterialCodes, setCertifiedRawMaterialCodes] = useState<
      any[]
    >([]);
  const [filteredRMCodes, setFilteredRMCodes] = useState<any[]>([]);
  const { cbLicensingCode, getUserInfo } = useTransactionCertificateStore();

  // State to track if user is a CO (Certified Organization)
  const [isCOUser, setIsCOUser] = useState(false);
  const { recentTEIds, saveRecentlySelectedTEId } = useRecentTEIds();

  useEffect(() => {
    if (isCOUser) {
      setFilteredRMCodes(rawMaterials);
      setDisable((prev) => ({
        ...prev,
        coTeId: true,
        rmCode: false,
      }));
    }
  }, [isCOUser, rawMaterials]);
  // CO User Detection and Auto-fill Logic
  useEffect(() => {
    const checkCOUser = async () => {
      const userEmail = cbLicensingCode;
      if (userEmail) {
        const extractedTEId = extractTEIdFromEmail(userEmail);
        if (extractedTEId) {
          setIsCOUser(true);
          
          // Auto-fill CO fields with extracted TE-ID
          if (formRef.current) {
            formRef.current.setFieldValue("coTeID", extractedTEId);
          } else {
            // Retry after a short delay if form not ready
            setTimeout(() => {
              if (formRef.current) {
                formRef.current.setFieldValue("coTeID", extractedTEId);
              }
            }, 100);
          }
          
          // Fetch organization details using extracted TE-ID
          try {
            const queryParams: Record<string, any> = {
              query_param: extractedTEId,
              offset: 0,
            };
            const response = await buyerSellerDetailsService.getTEDetails(
              queryParams
            );
            if (response && response.data.length > 0) {
              const details = response.data[0];
              if (formRef.current) {
                formRef.current.setFieldValue("coName", details.account_name);
              }
            }
          } catch (error) {
            console.error("Error fetching CO organization details:", error);
            // Fields remain editable if API fails
          }
          
          // Set fields as disabled and hidden for CO users
          setVisible((prev) => ({
            ...prev,
            coName: false,
            coTeId: false,
            coNameLabel: false,
            coTeIdLabel: false,
          }));
          setDisable((prev) => ({
            ...prev,
            coName: true,
            coTeId: true,
          }));
        }
      }
    };
    
    checkCOUser();
  }, [cbLicensingCode]);

  const getRawMatStandardCodes = async () => {
      const response = await executeGet(
        TCAppConfig.GET_CERTIFIED_RAW_MATERIAL_CODES
      );
      if (response?.data) {
        setCertifiedRawMaterialCodes(response.data);
      }
    };
  useEffect(() => {
    const fetchRawMaterialOptions = async () => {
      try {
        const result: any = await dtrackItService.getRawMaterialCodes();
        const options = result.data.map((t: any) => ({
          value: t.code_1,
          label: `${t.description_1} (${t.code_1})`,
        }));
        setRawMaterials(options);
      } catch (err) {
        console.error("Error fetching raw material codes", err);
      }
    };
    fetchRawMaterialOptions();
    getRawMatStandardCodes();
  }, []);

  // Refetch data when pagination changes
  useEffect(() => {
    if (showTable && formRef.current?.values) {
      const currentValues = formRef.current.values;
      if (currentValues.coTeID) {
        fetchInventoryUpdatesData(currentValues);
      }
    }
  }, [pagination?.pageIndex, pagination?.pageSize]);

  const handleSubmit = () => {
    if (formRef.current) {
      formRef.current.submitForm(); // Programmatically submit the form
      setResetEnable(true);
    }
  };
  const handleView = (row: any) => {
    navigate(
      `/ml/material-ledger/${row.original.teId}/${row.original.certifiedRmCode}`
    );
  };

  const handleEdit = (row: any) => {
    navigate(
      `/ml/inventory-updates/${row.original.teId}/${row.original.certifiedRmCode}`
    );
  };
  
  const updateFilteredRMCodes = (value: any) => {
    // Safely handle cases where standards are missing or not in the expected format
    if (!value || !value.standards) {
      setFilteredRMCodes([]);
      return;
    }

    const standardsString =
      Array.isArray(value.standards) || typeof value.standards === "string"
        ? String(value.standards)
        : "";

    const matches = standardsString
      ? [...standardsString.matchAll(/\(([^)]+)\)/g)]
      : [];

    const codes = matches.map((match) => match[1]).filter(Boolean);

    if (!codes.length) {
      setFilteredRMCodes([]);
      return;
    }

    const approvedSet = new Set(codes);
    const filtered = certifiedRawMaterialCodes.filter((item) =>
      String(item?.standard || "")
        .split("/")
        .some((part: any) => approvedSet.has(String(part).trim()))
    );

    const validMaterialCodes = filtered.map((item) => item.material_code);
    setFilteredRMCodes(
      rawMaterials.filter((item) => validMaterialCodes.includes(item.value))
    );
  };

  const fetchInventoryUpdatesData = async (values: any) => {
    try {
      // Extract startDate and endDate from the date range
      const params: any = {
        teId: values.coTeID,
        ...(values.rawMaterialCode?.length > 0 && {
          rawMaterialCode: JSON.stringify(values.rawMaterialCode),
        }),
        cbLicensingCode: cbLicensingCode,
        limit: pagination?.pageSize,
        offset: pagination?.pageIndex * pagination?.pageSize,
      };

      // Add date range parameters if transactionDate is provided
      if (values.transactionDate && values.transactionDate[0] && values.transactionDate[1]) {
        params.startDate = moment(values.transactionDate[0]).format('YYYY-MM-DD');
        params.endDate = moment(values.transactionDate[1]).format('YYYY-MM-DD');
      }

      const response = await executeGet(TCAppConfig.GET_RAW_MATERIAL_SUMMARY, params);

      if (response?.data ) {
        setData(mapApiResponseToRawMaterial(response.data.response));
        setRowCount(response.data.no_of_records || 0);
        setShowTable(true);
      } else {
        setData([]);
        setRowCount(0);
      }
    } catch (error) {
      console.error("Error fetching data:", error);

      setData([]);
      setRowCount(0);
    }
  };

  const handleCoNameValueChange = (value: any) => {
    if (value) {
      updateFilteredRMCodes(value);
      formRef.current.setFieldValue("coName", value.account_name);
      formRef.current.setFieldValue("coTeID", value.TE_ID);
      setVisible((prev) => ({
        ...prev,
        coTeId: false,
        coNameLabel: false,
      }));
      setDisable((prev) => ({
        ...prev,
        coTeId: true,
        rmCode: !value?.TE_ID,
      }));
    } else {
      setFilteredRMCodes([]);
      formRef.current.setFieldValue("coName", "");
      formRef.current.setFieldValue("coTeID", "");
      setVisible((prev) => ({
        ...prev,
        coTeId: true,
        coNameLabel: true,
      }));
      setDisable((prev) => ({
        ...prev,
        coTeId: false,
        rmCode: true,
      }));
    }
  };

  const handleCoTeIdValueChange = (value: any) => {
    if (value) {
      // Save selected TE details to recent list
      saveRecentlySelectedTEId(value);
      updateFilteredRMCodes(value);
      formRef.current.setFieldValue("coTeID", value.TE_ID);
      formRef.current.setFieldValue("coName", value.account_name);
      setVisible((prev) => ({
        ...prev,
        coName: false,
        coTeIdLabel: false,
      }));
      setDisable((prev) => ({
        ...prev,
        coName: true,
        rmCode: !value?.TE_ID,
      }));
    } else {
      setFilteredRMCodes([]);
      formRef.current.setFieldValue("coTeID", "");
      formRef.current.setFieldValue("coName", "");
      setVisible((prev) => ({
        ...prev,
        coName: true,
        coTeIdLabel: true,
      }));
      setDisable((prev) => ({
        ...prev,
        coName: false,
        rmCode: true,
      }));
    }
  };
  const fetchTENameDetails = async (
    query: string,
    offset: number,
    type: string
  ) => {
    try {
      const queryParams: Record<string, any> = {
        query_param: "",
        offset: offset,
        name: query,
        tcCbLicensingCode:  cbLicensingCode,
      };
      const response = await mlbuyerSellerDetailsService.getMLValidatedCODetails(
        queryParams
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching TE details by name:", error);
      return [];
    }
  };

  const fetchTEIdDetails = async (
    query: string,
    offset: number,
    type: string
  ) => {
    try {
      const queryParams: Record<string, any> = {
        query_param: query,
        offset: offset,
        name: "",
        tcCbLicensingCode: cbLicensingCode,
      };
      const response = await mlbuyerSellerDetailsService.getMLValidatedCODetails(
        queryParams
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching TE details by ID:", error);
      return [];
    }
  };

  const handleReset = async () => {
    formRef.current.resetForm();
    
    // Reset visibility and disable states only for non-CO users
    if (!isCOUser) {
      setVisible({
        coName: true,
        coTeId: true,
        coNameLabel: true,
        coTeIdLabel: true,
      });
      
      setDisable({
        coName: false,
        coTeId: false,
        rmCode:true
      });
    } else {
      // For CO users, re-populate their organization details
      const userEmail = getUserInfo?.ID;
      if (userEmail) {
        const extractedTEId = extractTEIdFromEmail(userEmail);
        if (extractedTEId) {
          setTimeout(async () => {
            if (formRef.current) {
              formRef.current.setFieldValue("coTeID", extractedTEId);
              
              // Refetch organization details
              try {
                const queryParams: Record<string, any> = {
                  query_param: extractedTEId,
                  offset: 0,
                };
                const response = await buyerSellerDetailsService.getTEDetails(
                  queryParams
                );
                if (response && response.data.length > 0) {
                  const details = response.data[0];
                  setFilteredRMCodes(rawMaterials);;
                  setDisable((prev) => ({
                    ...prev,
                    coTeId: true,
                    rmCode: false,
                  }));
                  formRef.current.setFieldValue("coName", details.account_name);
                }
              } catch (error) {
                console.error("Error refetching CO organization details:", error);
              }
            }
          }, 100);
        }
      }
    }
    
    setResetEnable(false);
    setShowTable(false);
  };

  const formConfig = {
    pageName: "inventory-dashboard-page",
    initialValues: {
      coName: "",
      coTeID: "",
      rawMaterialCode: [],
      transactionDate: [null, null],
    },
    validationSchema: Yup.object().shape({
      coName: Yup.string().nullable(),
      coTeID: Yup.string().required("TE-ID is required"),
      rawMaterialCode: Yup.array().of(Yup.string()).nullable(),
    }),
    sections: [
      {
        fields: [
          {
            label: "Certified Organization Name",
            name: "coName",
            type: "search",
            gridSize: { xs: 12, md: 3 },
            required: false,
            visible: () => visible.coName && !isCOUser,
            props: {
              placeholder: "Search Organization Name",
              variant: "outlined",
              fullWidth: true,
              displayKey: "account_name",
              onChange: handleCoNameValueChange,
              onSearch: (query: string, offset: number) => {
                return fetchTENameDetails(query, offset, "name");
              },
            },
          },
          {
            label: "Certified Organization Name",
            name: "coName",
            type: "text",
            gridSize: { xs: 12, md: 3 },
            required: false,
            visible: () => !visible.coName || isCOUser,
            disabled: disable.coName || isCOUser,
            props: {
              placeholder: "Enter Organization Name",
              variant: "outlined",
              fullWidth: true,
            },
          },
          {
            label: "Certified Organisation TE-ID",
            name: "coTeID",
            type: "search",
            gridSize: { xs: 12, md: 3 },
            required: true,
            visible: () => visible.coTeId && !isCOUser,
            props: {
              placeholder: "Search by TE-ID",
              variant: "outlined",
              fullWidth: true,
              displayKey: "TE_ID",
              restrictInputFormat: visible.coTeIdLabel,
              onChange: handleCoTeIdValueChange,
              // Show recently selected TE IDs when the field is focused and empty
              fetchOnEmptyFocus: true,
              onSearch: (query: string, offset: number) => {
                // If query is empty, return recent TE IDs (if any)
                if (!query || query.trim() === "") {
                  return Promise.resolve(
                    recentTEIds.length > 0 ? recentTEIds : []
                  );
                }
                return fetchTEIdDetails(query, offset, "query");
              },
            },
          },
          {
            label: "Certified Organisation TE-ID",
            name: "coTeID",
            type: "text",
            gridSize: { xs: 12, md: 3 },
            required: true,
            visible: () => !visible.coTeId || isCOUser,
            disabled: disable.coTeId || isCOUser,
            props: {
              placeholder: "Enter TE-ID",
              variant: "outlined",
              fullWidth: true,
            },
          },
          {
            label: "Raw Material Code",
            name: "rawMaterialCode",
            type: "select",
            gridSize: { xs: 12, md: 3 },
            disabled: disable?.rmCode,
            required: false,
            props: {
              options: filteredRMCodes,
              enableSearch: true,
              placeholder: "Select Raw Material Code",
              variant: "outlined",
              fullWidth: true,
              multiple: true,
            },
          },
          {
            label: "Last Updated Date",
            name: "transactionDate",
            type: "dateRange",
            gridSize: { xs: 14, md: 4 },
            required: false,
            props: {
              // options: rawMaterials,
              // enableSearch: true,
              // placeholder: "Select Raw Material Code",
              variant: "outlined",
              fullWidth: true,
              multiple: true,
            },
          },
          {
            label: "",
            name: "",
            type: "component",
            gridSize: { xs: 12, md: 3, lg: 3 },
            required: false,
            value: "",
            children: (
              <>
                <Box marginTop="23px" marginLeft="10px">
                  <StyledButton onClick={handleSubmit}>Search</StyledButton>
                  {isResetEnabled ? (
                    <StyledButton onClick={handleReset}>Reset</StyledButton>
                  ) : null}
                </Box>
              </>
            ),
            props: {},
          },
        ],
      },
    ],
    handleSubmit: async (values: any) => {
      if (values.coTeID) {
        await fetchInventoryUpdatesData(values);
      } else {
        setShowTable(false);
      }
    },
  };
  const columns = [
    {
      accessorKey: "certifiedRmCode",
      header: "Raw Material Code",
      minSize: 60,
      maxSize: 150,
    },
    {
      accessorKey: "rmDescription",
      header: "Raw Material Description",
      minSize: 60,
      maxSize: 100,
    },
    {
      accessorKey: "balance",
      header: "Certified Weight (Avaialble) kg",
      minSize: 80,
      maxSize: 150,
    },
    {
      accessorKey: "lastUpdated",
      header: "Last Updated Date",
      minSize: 80,
      maxSize: 120,
      Cell: ({ cell }: { cell: any }) => {
        const date = cell.getValue(); // Access the raw date value
        const formattedDate = moment(date).isValid()
          ? moment(date).format("YYYY-MM-DD") // Format valid date
          : date; // Fallback to raw value if invalid
        return <span>{formattedDate}</span>;
      },
    },
  ];

  return (
    <PageWrapper>
      <Typography
        mt={4}
        variant="h6"
        gutterBottom
        align="left"
        fontWeight="600"
      >
        Raw Material Summary
      </Typography>
      <FormBuilder formConfig={formConfig} formRef={formRef} />
      {loading ? (
        <LoaderComponent />
      ) : showTable ? (
        <>
          <Box display="grid">
            <CustomTable
              columns={columns}
              data={data}
              enableTopToolbar={true}
              enableBottomToolbar={true}
              paginations={pagination} // Pass current pagination state
              setPaginations={setPagination}
              rowCount={rowCount}
              isLoadingTodos={loading}
              enableRowActions={true}
              renderRowActions={({ row }) => (
                <ActionsMenu
                  row={row}
                  onView={() => handleView(row)}
                  {...(!isViewOnlyAdminMode() && { onEdit: () => handleEdit(row) })}
                />
              )}
            />
          </Box>
        </>
      ) : null}
    </PageWrapper>
  );
};

export default RawMaterialSummarry;
